import { z } from 'zod';

import { GenderSchema, type Gender } from '../../users/gender';

/**
 * The scope dimensions a setting value can be stored under (U9/U15).
 *
 * **Ordered from least to most specific**, which is the order resolution walks
 * in reverse: the most specific stored value wins, `global` is the last stored
 * candidate, and the registry's coded default is what remains. A future
 * dimension (`country`) is appended here and to a definition's `scopesAllowed`
 * — resolution and every call site stay untouched.
 */
export const SETTING_SCOPE_TYPES = ['global', 'gender'] as const;

export const SettingScopeTypeSchema = z.enum(SETTING_SCOPE_TYPES);

export type SettingScopeType = (typeof SETTING_SCOPE_TYPES)[number];

/**
 * The two scope fields, **unrefined**, so every schema that carries a scope
 * (a stored value, a write request, an effective-value row) extends the same
 * shape instead of re-declaring it.
 */
export const SettingScopeFieldsSchema = z.object({
  scopeType: SettingScopeTypeSchema,
  scopeValue: z.string().nullable().default(null),
});

/**
 * The scope's cross-field law, shared by every schema built on the fields
 * above: `global` carries no value and `gender` must carry a supported one.
 *
 * Without it a `{global, 'male'}` row would be a second global that the
 * database's partial unique index accepts happily, and resolution would pick
 * between the two by insertion order.
 */
export function refineSettingScope(
  scope: { scopeType: SettingScopeType; scopeValue: string | null },
  ctx: z.RefinementCtx,
): void {
  if (scope.scopeType === 'global') {
    if (scope.scopeValue !== null) {
      ctx.addIssue({
        code: 'custom',
        path: ['scopeValue'],
        message: 'The global scope carries no scopeValue.',
      });
    }
    return;
  }

  if (!GenderSchema.safeParse(scope.scopeValue).success) {
    ctx.addIssue({
      code: 'custom',
      path: ['scopeValue'],
      message: 'A gender-scoped value requires scopeValue to be a supported gender.',
    });
  }
}

/** A concrete scope: the dimension plus the value within it. */
export const SettingScopeSchema = SettingScopeFieldsSchema.strict().superRefine(refineSettingScope);

export type SettingScope = z.infer<typeof SettingScopeSchema>;

/** The global scope — the value every member resolves unless a scope overrides it. */
export function globalScope(): SettingScope {
  return { scopeType: 'global', scopeValue: null };
}

/** The scope of one gender's override (U15 — gender travels as data, never as an `if`). */
export function genderScope(gender: Gender): SettingScope {
  return { scopeType: 'gender', scopeValue: gender };
}

/** Whether two scopes address the same row. */
export function isSameScope(a: SettingScope, b: SettingScope): boolean {
  return a.scopeType === b.scopeType && a.scopeValue === b.scopeValue;
}

/**
 * The scope's rendering inside a cache key or a log line — defined **once**
 * here so the api, the admin UI and any future consumer cannot disagree about
 * what identifies a scope.
 */
export function scopeCacheSuffix(scope: SettingScope): string {
  return scope.scopeValue === null ? scope.scopeType : `${scope.scopeType}:${scope.scopeValue}`;
}
